import type { Column } from "@emkit/builder-core";
import { useEditor } from "../../store.js";
import { readAtBreakpoint, writeAtBreakpoint } from "../../utils/responsive.js";
import { AdvancedFields } from "./AdvancedFields.js";
import { NumberField } from "./fields.js";

interface Props {
  column: Column;
  sectionId: string;
  sectionColumnCount: number;
}

const BP_LABEL: Record<string, string> = {
  desktop: "desktop",
  tablet: "tablet",
  mobile: "mobile",
};

export function ColumnInspector({ column, sectionId, sectionColumnCount }: Props) {
  const update = useEditor((s) => s.updateColumn);
  const remove = useEditor((s) => s.removeColumn);
  const addColumn = useEditor((s) => s.addColumn);
  const moveColumn = useEditor((s) => s.moveColumn);
  const duplicateColumn = useEditor((s) => s.duplicateColumn);
  const breakpoint = useEditor((s) => s.breakpoint);
  const columnsInSection = useEditor(
    (s) => s.page.sections.find((sec) => sec.id === sectionId)?.columns ?? [],
  );
  const colIndex = columnsInSection.findIndex((c) => c.id === column.id);
  const canMoveLeft = colIndex > 0;
  const canMoveRight = colIndex < columnsInSection.length - 1;

  const span = readAtBreakpoint<number>(column.span, breakpoint) ?? 12;
  const gap = readAtBreakpoint<number>(column.gap, breakpoint) ?? 16;

  const setSpan = (v: number) => {
    const clamped = Math.max(1, Math.min(Math.round(v), 12));
    update(column.id, {
      span: writeAtBreakpoint<number>(column.span, breakpoint, clamped),
    });
  };

  const setGap = (v: number) => {
    update(column.id, {
      gap: writeAtBreakpoint<number>(column.gap, breakpoint, v),
    });
  };

  return (
    <aside className="emke-inspector">
      <div className="emke-section-title">
        Column · <span style={{ color: "var(--accent)" }}>{column.id}</span>
      </div>

      <div style={{ padding: "0 16px 8px", display: "flex", gap: 4 }}>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveColumn(sectionId, column.id, "left")}
          disabled={!canMoveLeft}
          style={{ flex: 1, opacity: canMoveLeft ? 1 : 0.4 }}
          title="Move column left"
        >
          ←
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveColumn(sectionId, column.id, "right")}
          disabled={!canMoveRight}
          style={{ flex: 1, opacity: canMoveRight ? 1 : 0.4 }}
          title="Move column right"
        >
          →
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => duplicateColumn(sectionId, column.id)}
          style={{ flex: 1 }}
          title="Duplicate column"
        >
          ⎘
        </button>
      </div>

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Size ({BP_LABEL[breakpoint]})
      </div>
      <NumberField
        label={`Span (${span} / 12)`}
        value={span}
        min={1}
        max={12}
        onChange={setSpan}
      />
      <NumberField
        label="Gap between widgets (px)"
        value={gap}
        min={0}
        max={200}
        onChange={setGap}
      />

      <div
        style={{
          padding: "12px 16px 0",
          display: "flex",
          gap: 6,
          flexDirection: "column",
        }}
      >
        <button
          type="button"
          className="emke-btn"
          onClick={() => addColumn(sectionId, column.id)}
        >
          + Add column after this
        </button>
      </div>

      <AdvancedFields
        value={column}
        onChange={(patch) => update(column.id, patch)}
      />

      <div
        style={{
          padding: "12px 16px 16px",
          borderTop: "1px solid var(--border)",
          marginTop: 16,
        }}
      >
        <button
          type="button"
          className="emke-btn is-danger"
          onClick={() => remove(sectionId, column.id)}
          disabled={sectionColumnCount <= 1}
          title={
            sectionColumnCount <= 1
              ? "Section must have at least one column"
              : "Delete this column (removes its widgets)"
          }
          style={{
            width: "100%",
            opacity: sectionColumnCount <= 1 ? 0.4 : 1,
          }}
        >
          Delete column
        </button>
      </div>
    </aside>
  );
}
